// frontend/src/features/ups/modals/DriverDetailsModal.js
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  uploadDriverPhoto as svcUpload,
  deleteDriverPhoto as svcDelete,
} from '../../../services/driverService';
import {
  upsertDriver,
  selectDriverById,
} from '../../drivers/driverSlice';

import MoveSiteModal from './MoveSiteModal'; 
import BannedDriverEditModal from '../BannedDriverEditModal';

export default function DriverDetailsModal({
  driver,
  onClose,
  onMoveSuccess,
  refreshAssignmentsAfterMove = true,
  allowMoveSite = true,
}) {
  const closeBtnRef = useRef(null);
  const dispatch = useDispatch();

  const [isBusy, setIsBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Nested modal states
  const [showMoveSiteModal, setShowMoveSiteModal] = useState(false);
  const [showBanEditModal, setShowBanEditModal] = useState(false);

  const liveDriver = useSelector((state) =>
    driver?.id != null ? selectDriverById(state, driver.id) : null
  );

  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  // Escape handling with nested modal protection
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !showMoveSiteModal && !showBanEditModal) {
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, showMoveSiteModal, showBanEditModal]);

  const safe = (v) => v ?? '—';
  const safeDate = (v) => (v ? new Date(v).toLocaleString() : '—');

  const rowContext = driver || {};
  const driverCore = liveDriver || {};

  const driverData = {
    id: driverCore.id ?? rowContext.id,
    name: driverCore.name ?? rowContext.name,
    vehicle_registration_number:
      driverCore.vehicle_registration_number ?? rowContext.vehicle_registration_number,
    vrn: driverCore.vrn ?? rowContext.vrn,
    phone: driverCore.phone ?? rowContext.phone,
    email: driverCore.email ?? rowContext.email,
    agency_name: driverCore.agency_name ?? rowContext.agency_name,
    agency: driverCore.agency ?? rowContext.agency,
    company_id: driverCore.company_id ?? rowContext.company_id,
    workforce_source: driverCore.workforce_source ?? rowContext.workforce_source,
    submission_date: driverCore.submission_date ?? rowContext.submission_date,
    photo_url: driverCore.photo_url ?? rowContext.photo_url,
    photo_mime: driverCore.photo_mime ?? rowContext.photo_mime,
    photo_updated_at: driverCore.photo_updated_at ?? rowContext.photo_updated_at,
  };

  const assignmentContext = {
    assignment_id: rowContext.assignment_id ?? null,
    assignment_status: rowContext.assignment_status ?? null,
    starts_at: rowContext.starts_at ?? rowContext.start_time ?? null,
    ends_at: rowContext.ends_at ?? rowContext.end_time ?? null,
    site_id: rowContext.site_id ?? null,
    site_name: rowContext.site_name ?? null,
    current_state: rowContext.current_state ?? null,
    is_banned: rowContext.is_banned ?? null,
    ban_id: rowContext.ban_id ?? null,
    ban_reason: rowContext.ban_reason ?? null,
    banned_at: rowContext.banned_at ?? null,
    blocked_reason: rowContext.blocked_reason ?? rowContext.reason ?? null,
  };

  const { id, name, photo_url, photo_updated_at } = driverData;

  const ownershipLabel =
    driverData.workforce_source === 'COMPANY' || driverData.company_id
      ? 'Internal / Company-owned'
      : driverData.agency_name || driverData.agency?.name
      ? `Agency — ${driverData.agency_name || driverData.agency?.name}`
      : 'Agency';

  const hasMovableAssignment =
    !!assignmentContext.assignment_id &&
    ['requested', 'confirmed', 'active'].includes(
      String(assignmentContext.assignment_status || '').toLowerCase()
    );

  // This is the "Master Switch" for moving site
  const canEditBan = !!(assignmentContext.banned_at || assignmentContext.ban_reason);
  const canMoveSite = allowMoveSite && hasMovableAssignment;

  
  const showAdvancedOperations = canMoveSite || canEditBan || allowMoveSite;

  const operationalStatusLabel =
  assignmentContext.current_state || assignmentContext.assignment_status || '—';

  if (!id) return null;

  // Photo Handlers
  async function handleUploadPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsBusy(true);
      setErrorMsg('');
      const updated = await svcUpload(id, file);
      if (updated) dispatch(upsertDriver(updated));
    } catch (err) {
      setErrorMsg('Could not upload photo.');
    } finally {
      setIsBusy(false);
    }
  }

  async function handleRemovePhoto() {
    try {
      setIsBusy(true);
      setErrorMsg('');
      const updated = await svcDelete(id);
      if (updated) dispatch(upsertDriver(updated));
    } catch (err) {
      setErrorMsg('Could not remove photo.');
    } finally {
      setIsBusy(false);
    }
  }

  const ver = photo_updated_at ? new Date(photo_updated_at).getTime() : Date.now();
  const src = photo_url ? `${photo_url}?v=${ver}` : '/img/placeholder-avatar.png';

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40"
        onClick={() => {
          if (!showMoveSiteModal && !showBanEditModal) onClose?.();
        }}
        aria-hidden="true"
      >
        <div
          className="bg-white rounded-lg shadow-md p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Driver Details</h2>
            <button onClick={onClose} ref={closeBtnRef} className="text-gray-500 hover:text-gray-700 text-xl font-bold">&times;</button>
          </div>

          {errorMsg && <div className="mb-3 p-2 rounded bg-red-50 text-red-700 text-sm border border-red-200">{errorMsg}</div>}

          {/* Photo Section */}
          <div className="mb-4">
            <p className="font-semibold mb-2">Photo</p>
            <div className="flex items-start gap-3">
              <img src={src} alt="Driver" className="w-24 h-24 object-cover rounded border" />
              <div className="flex flex-col gap-2">
                <label className={`px-3 py-1 rounded text-white text-sm cursor-pointer ${isBusy ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
                  {isBusy ? 'Working...' : 'Upload photo'}
                  <input type="file" className="hidden" onChange={handleUploadPhoto} disabled={isBusy} />
                </label>
                {photo_url && (
                  <button onClick={handleRemovePhoto} disabled={isBusy} className="text-red-600 text-sm border border-red-300 px-3 py-1 rounded hover:bg-red-50">
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Core Fields */}
          <div className="space-y-2 text-sm">
            <p><strong>Name:</strong> {safe(name)}</p>
            <p><strong>Vehicle:</strong> {safe(driverData.vehicle_registration_number || driverData.vrn)}</p>
            <p><strong>Phone:</strong> {safe(driverData.phone)}</p>
            <p><strong>Email:</strong> {safe(driverData.email)}</p>
            <p><strong>Ownership:</strong> {ownershipLabel}</p>
            <p><strong>Submitted:</strong> {safeDate(driverData.submission_date)}</p>

            {/* Ban Info */}
            {(assignmentContext.banned_at || assignmentContext.ban_reason) && (
              <div className="mt-3 rounded border border-red-200 bg-red-50 p-3">
                <p className="font-semibold text-red-700">Ban Info</p>
                <p><strong>Banned At:</strong> {safeDate(assignmentContext.banned_at)}</p>
                <p><strong>Reason:</strong> {safe(assignmentContext.ban_reason)}</p>
              </div>
            )}

            {/* Operational Context */}
            <div className="mt-4 p-3 rounded border border-gray-200 bg-gray-50">
              <p className="font-semibold text-gray-800 mb-1">Operational Context</p>
              <p><strong>Status:</strong> <span className="capitalize">{operationalStatusLabel}</span></p>
              <p><strong>Current Site:</strong> {safe(assignmentContext.site_name)}</p>
              
              {/* Blocked Reason Restore */}
              {assignmentContext.blocked_reason && (
                <p className="text-orange-700 mt-2">
                  <strong>Blocked Reason:</strong> {assignmentContext.blocked_reason}
                </p>
              )}
            </div>
          </div>

          {/* Advanced Operations */}
          {showAdvancedOperations && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="font-semibold text-gray-800 mb-3">Advanced Operations</p>
              <div className="flex gap-2 flex-wrap">
                {allowMoveSite && (
                  <button
                    onClick={() => setShowMoveSiteModal(true)}
                    disabled={!canMoveSite}
                    className={`px-4 py-2 rounded font-medium ${
                      canMoveSite
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Move Site
                  </button>
                )}

                {canEditBan && (
                  <button
                    onClick={() => setShowBanEditModal(true)}
                    className="px-4 py-2 rounded font-medium bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  >
                    Edit Ban
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end mt-6">
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium">
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Nested Modals */}
      {showMoveSiteModal && (
        <MoveSiteModal
          driverData={driverData}
          assignmentContext={assignmentContext}
          refreshAssignmentsAfterMove={refreshAssignmentsAfterMove}
          onClose={() => setShowMoveSiteModal(false)}
          onMoveSuccess={() => {
            setShowMoveSiteModal(false);
            onMoveSuccess?.();
            onClose?.();
          }}
        />
      )}

      {showBanEditModal && (
        <BannedDriverEditModal
          ban={{
            ...driverData,
            ...assignmentContext,
            ban_id: assignmentContext.ban_id,
          }}
          onClose={() => setShowBanEditModal(false)}
          onUpdate={() => {
            setShowBanEditModal(false);
            onClose?.(); 
          }}
        />
      )}
    </>
  );
}
